import { useMemo } from 'react';
import { useAuthStore } from '@/shared/store/auth.store';
import type {
  PasoGuia,
  TourDefinicion,
} from '@/features/guia-interactiva/types/guia-interactiva.types';

/**
 * Filtra los pasos de un tour por los permisos del usuario:
 *   - Si el tour declara `permisoMenu` y el usuario no lo tiene, devuelve [].
 *   - Descarta cada paso cuyo `permisoRequerido` el usuario no tenga.
 *
 * Selector de zustand estable: devolver `?? []` dentro del selector crearía
 * un array nuevo en cada render y rompería la memoización aguas abajo.
 */
export function usePasosFiltrados(tour: TourDefinicion | null | undefined): PasoGuia[] {
  const permisos = useAuthStore(state => state.user?.permisos);

  return useMemo(() => {
    if (!tour) return [];
    const set = new Set(permisos ?? []);
    if (tour.permisoMenu && !set.has(tour.permisoMenu)) return [];
    return tour.pasos.filter(p => !p.permisoRequerido || set.has(p.permisoRequerido));
  }, [tour, permisos]);
}

/**
 * Tours a los que el usuario tiene acceso efectivo (al menos 1 paso visible).
 * Útil para el índice de tours del navbar.
 */
export function useToursDisponibles(tours: readonly TourDefinicion[]): TourDefinicion[] {
  const permisos = useAuthStore(state => state.user?.permisos);

  return useMemo(() => {
    const set = new Set(permisos ?? []);
    return tours.filter(tour => {
      if (tour.permisoMenu && !set.has(tour.permisoMenu)) return false;
      const visibles = tour.pasos.filter(p => !p.permisoRequerido || set.has(p.permisoRequerido));
      return visibles.length > 0;
    });
  }, [tours, permisos]);
}
